import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import type { SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar-overrides.css';
import { useCalendarStore } from '../hooks/useCalendarStore';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarEventCard } from './CalendarEventCard';
import { CalendarDateHeader } from './CalendarDateHeader';
import { CalendarShowMore } from './CalendarShowMore';
import type { CalendarEvent, CalendarView as CalendarViewType } from '../types';
import { useDateFormat } from '@/shared/hooks/useDateFormat';

const locales = { es, 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarViewProps {
  onEventClick?: (event: CalendarEvent) => void;

  // ── Modo embedded (tablero de proyecto / "mis tareas") ────────────────────
  // Cuando se pasan, el componente NO lanza queries propias.
  events?: CalendarEvent[];
  isLoading?: boolean;
  isError?: boolean;
  // Estado controlado de vista y fecha (el padre gestiona su propio state)
  controlledView?: CalendarViewType;
  controlledDate?: Date;
  onViewChange?: (view: CalendarViewType) => void;
  onDateChange?: (date: Date) => void;
  onRangeChange?: (range: { start: Date; end: Date }) => void;
  // Oculta filtros de tipo del toolbar
  hideToolbarExtras?: boolean;
  // Oculta el selector de vista mes/semana/día/agenda (ej. consumidores solo-mes como ausencias)
  hideViewSelector?: boolean;
  // Selección de un slot vacío en la grilla (solo modo autónomo) — el padre decide qué hacer,
  // p. ej. abrir el sheet de creación de evento con esta fecha/hora como default.
  onSlotSelect?: (slot: { start: Date; end: Date }) => void;
}

export function CalendarView({
  onEventClick,
  events: externalEvents,
  isLoading: externalLoading,
  isError: externalError,
  controlledView,
  controlledDate,
  onViewChange,
  onDateChange,
  onRangeChange,
  hideToolbarExtras,
  hideViewSelector,
  onSlotSelect,
}: CalendarViewProps) {
  const { i18n, t } = useTranslation('calendar');
  const { timeFormat } = useDateFormat();

  // Modo embedded: cuando se proveen eventos externos no se lanzan queries propias
  const isEmbedded = externalEvents !== undefined;

  const store = useCalendarStore();
  const internal = useCalendarEvents(!isEmbedded);

  // Resolución de fuente de datos y estado según el modo activo
  const resolvedEvents = isEmbedded ? externalEvents : internal.events;
  const resolvedLoading = isEmbedded ? (externalLoading ?? false) : internal.isLoading;
  const resolvedError = isEmbedded ? (externalError ?? false) : internal.isError;
  const resolvedView = isEmbedded ? (controlledView ?? 'month') : store.view;
  const resolvedDate = isEmbedded ? (controlledDate ?? new Date()) : store.currentDate;

  const eventCountByDay = (() => {
    const map = new Map<string, number>();
    resolvedEvents.forEach((e) => {
      const key = format(e.start, 'yyyy-MM-dd');
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  })();

  // Resolver la locale de date-fns directamente — no depender del mapeo culture→locales
  // del localizer porque i18n.language puede retornar 'es', 'es-ES', 'es-MX', etc.
  const dateFnsLocale = i18n.language.startsWith('es') ? es : enUS;

  function handleRangeChange(range: Date[] | { start: Date; end: Date }) {
    const normalized = Array.isArray(range)
      ? { start: range[0], end: range[range.length - 1] }
      : range;
    if (isEmbedded) onRangeChange?.(normalized);
    else store.setVisibleRange(normalized);
  }

  function handleSelectEvent(event: CalendarEvent) {
    onEventClick?.(event);
  }

  function handleSlotSelect(slotInfo: SlotInfo) {
    onSlotSelect?.({ start: slotInfo.start, end: slotInfo.end });
  }

  function handleView(nextView: string) {
    const knownViews: CalendarViewType[] = ['month', 'week', 'work_week', 'day', 'agenda'];
    if (!knownViews.includes(nextView as CalendarViewType)) return;
    const v = nextView as CalendarViewType;
    if (isEmbedded) onViewChange?.(v);
    else store.setView(v);
  }

  function handleNavigate(newDate: Date) {
    if (isEmbedded) onDateChange?.(newDate);
    else store.setCurrentDate(newDate);
  }

  if (resolvedError) {
    return (
      <div
        className="flex h-64 items-center justify-center rounded-lg border border-border text-sm text-destructive"
        data-testid="calendar-error-message"
      >
        {t('error')}
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card"
      data-testid="calendar-view"
    >
      {/* Estado vacío — badge flotante no bloqueante, el toolbar sigue accesible */}
      {!resolvedLoading && resolvedEvents.length === 0 && (
        <div
          className="pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
          data-testid="calendar-empty-state"
        >
          <span className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground shadow-sm">
            {t('empty')}
          </span>
        </div>
      )}

      {/* Skeleton de carga — cubre solo mientras llega la primera respuesta */}
      {resolvedLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-card/80 backdrop-blur-[2px]">
          <span className="text-sm text-muted-foreground">{t('loading')}</span>
        </div>
      )}

      <Calendar<CalendarEvent>
        localizer={localizer}
        events={resolvedEvents}
        date={resolvedDate}
        onNavigate={handleNavigate}
        view={resolvedView}
        views={['month', 'week', 'work_week', 'day', 'agenda']}
        onView={handleView as unknown as (view: import('react-big-calendar').View) => void}
        onRangeChange={handleRangeChange}
        onSelectEvent={handleSelectEvent}
        selectable={!isEmbedded}
        onSelectSlot={!isEmbedded ? handleSlotSelect : undefined}
        culture={i18n.language}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        scrollToTime={new Date()}
        min={new Date(0, 0, 0, 7, 0, 0)}
        max={new Date(0, 0, 0, 21, 0, 0)}
        step={30}
        timeslots={1}
        formats={{
          timeGutterFormat: (date: Date) => format(date, timeFormat, { locale: dateFnsLocale }),
          agendaTimeFormat: (date: Date) => format(date, timeFormat, { locale: dateFnsLocale }),
          agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
            `${format(start, timeFormat, { locale: dateFnsLocale })} – ${format(end, timeFormat, { locale: dateFnsLocale })}`,
        }}
        components={{
          toolbar: (props) => (
            <CalendarToolbar
              label={props.label}
              onNavigate={props.onNavigate}
              onView={props.onView as unknown as (view: CalendarViewType) => void}
              hideFilters={isEmbedded || hideToolbarExtras}
              hideViewSelector={hideViewSelector}
            />
          ),
          event: ({ event }) => <CalendarEventCard event={event} />,
          month: {
            dateHeader: (props) => (
              <CalendarDateHeader
                {...props}
                eventCount={eventCountByDay.get(format(props.date, 'yyyy-MM-dd')) ?? 0}
              />
            ),
          },
          showMore: (props) => (
            <CalendarShowMore
              count={props.count}
              remainingEvents={props.remainingEvents as CalendarEvent[]}
              onEventClick={handleSelectEvent}
            />
          ),
        }}
        style={{ height: '100%' }}
      />
    </div>
  );
}
